import { ProchainesInterventions } from '../../Composants/prochaines-interventions/prochaines-interventions';
import { InterventionActualisation } from '../../Services/intervention-actualisation';
import { ErreurApi, messageErreurApi } from '../../Services/api-error';
import {
  Component,
  computed,
  effect,
  inject,
  signal,
  untracked,
  ChangeDetectionStrategy,
  DestroyRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { MatMenuModule } from '@angular/material/menu';
import { SpinnerOverlayComponent } from '../../Composants/spinner-overlay/spinner-overlay';
import { OverlayModule } from '@angular/cdk/overlay';
import { A11yModule } from '@angular/cdk/a11y';
import {
  SiloTickets,
  TicketCardItem,
  TicketColumnState,
} from '../../Composants/silo-tickets/silo-tickets';
import { EtatService } from '../../Services/etat';
import { ParcService } from '../../Services/parc';
import { Installation as InstallationService } from '../../Services/installation';
import { TicketState } from '../../Services/ticket-state';
import {
  extrairePagePagination,
  extraireTotalPagination,
  extraireTotalPagesPagination,
} from '../../Services/pagination';
import { ResultatChargementSilo, TicketSilo, TicketsResponse } from './tickets.types';
import { adapterTicket, extraireTickets } from './ticket-adapter';
import { TicketDragService } from './ticket-drag.service';
import { FiltreTickets, OptionsTickets, TriTickets } from '../../Services/tickets-query';
import { FILTRES_TICKETS, GROUPES_FILTRES_TICKETS, TRIS_TICKETS } from './tickets-options';

@Component({
  selector: 'app-tickets',
  imports: [
    ProchainesInterventions,
    SiloTickets,
    MatMenuModule,
    OverlayModule,
    A11yModule,
    SpinnerOverlayComponent,
  ],
  providers: [TicketDragService],
  templateUrl: './tickets.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './tickets.css',
})
export class Tickets {
  private readonly actualisationInterventions = inject(InterventionActualisation);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly parcService = inject(ParcService);
  private readonly installationService = inject(InstallationService);
  readonly etat = inject(EtatService);
  readonly drag = inject(TicketDragService);
  private readonly destroyRef = inject(DestroyRef);
  private chargementId = 0;
  readonly chargement = signal(false);

  private requeteValide() {
    const id = this.chargementId;
    const installation = this.routeInstallationId();
    const parc = this.etat.parcSelectionne();
    const recherche = this.etat.filtreTexteTickets();
    const options = this.optionsTickets();
    return () =>
      !this.destroyRef.destroyed &&
      id === this.chargementId &&
      installation === this.routeInstallationId() &&
      parc === this.etat.parcSelectionne() &&
      recherche === this.etat.filtreTexteTickets() &&
      options === this.optionsTickets();
  }

  private readonly ticketsPage = 1;
  private readonly ticketsLimit = 10;

  readonly filtresDisponibles = FILTRES_TICKETS;
  readonly trisDisponibles = TRIS_TICKETS;
  readonly filtresActifs = signal<readonly FiltreTickets[]>([]);
  readonly panneauFiltresOuvert = signal(false);
  readonly brouillonFiltres = signal<readonly FiltreTickets[]>([]);
  readonly groupesFiltres = GROUPES_FILTRES_TICKETS;
  readonly triActif = signal<TriTickets>('date');
  readonly optionsTickets = computed<OptionsTickets>(() => ({
    filtres: this.filtresActifs(),
    tri: this.triActif(),
  }));
  readonly libellesFiltresActifs = computed(() =>
    this.filtresDisponibles.filter((filtre) => this.filtresActifs().includes(filtre.valeur)),
  );

  readonly routeInstallationId = signal<string | null>(this.route.snapshot.paramMap.get('id'));

  readonly silos = signal<TicketSilo[]>([
    {
      title: 'Nouveaux',
      count: 0,
      badgeCount: 0,
      state: TicketState.nouveau,
      currentPage: 1,
      totalPages: 0,
      loadingMore: false,
      tickets: [],
    },
    {
      title: 'En cours',
      count: 0,
      badgeCount: 0,
      state: TicketState.encours,
      currentPage: 1,
      totalPages: 0,
      loadingMore: false,
      tickets: [],
    },
    {
      title: 'En attente',
      count: 0,
      badgeCount: 0,
      state: TicketState.enattente,
      currentPage: 1,
      totalPages: 0,
      loadingMore: false,
      tickets: [],
    },
    {
      title: 'Résolu',
      count: 0,
      badgeCount: 0,
      state: TicketState.resolu,
      currentPage: 1,
      totalPages: 0,
      loadingMore: false,
      tickets: [],
    },
  ]);

  private readonly routeSubscription = this.route.paramMap
    .pipe(takeUntilDestroyed())
    .subscribe((params) => {
      this.routeInstallationId.set(params.get('id'));
    });

  private readonly parcSelectionEffect = effect(() => {
    const sourceInstallation = this.lireInstallationDepuisRoute();
    const texte = this.etat.filtreTexteTickets();
    this.actualisationInterventions.revision();
    this.optionsTickets(); // Recharger quand les filtres appliqués ou le tri changent.
    if (sourceInstallation.estRouteInstallation) {
      const installationId = sourceInstallation.id;
      if (installationId == null) {
        this.reinitialiserSilos();
        return;
      }
      untracked(() => {
        void this.chargerTicketsParInstallation(installationId, texte);
      });
      return;
    }

    const parc = this.etat.parcSelectionne();
    if (!parc) {
      this.reinitialiserSilos();
      return;
    }
    untracked(() => {
      void this.chargerTicketsParParc(parc.id, texte);
    });
  });

  trackBySilo(_: number, silo: TicketSilo) {
    return silo.title;
  }

  ouvrirFiltres() {
    this.brouillonFiltres.set([...this.filtresActifs()]);
    this.panneauFiltresOuvert.set(true);
  }

  basculerBrouillonFiltre(filtre: FiltreTickets) {
    this.brouillonFiltres.update((filtres) =>
      filtres.includes(filtre)
        ? filtres.filter((valeur) => valeur !== filtre)
        : [...filtres, filtre],
    );
  }

  appliquerFiltres() {
    const selection = this.brouillonFiltres();
    const actifs = this.filtresActifs();
    if (
      selection.length !== actifs.length ||
      selection.some((filtre) => !actifs.includes(filtre))
    ) {
      this.filtresActifs.set([...selection]);
    }
    this.panneauFiltresOuvert.set(false);
  }

  basculerFiltre(filtre: FiltreTickets) {
    this.filtresActifs.update((filtres) =>
      filtres.includes(filtre)
        ? filtres.filter((valeur) => valeur !== filtre)
        : [...filtres, filtre],
    );
  }

  chargerPageSuivante(state: TicketColumnState) {
    const estValide = this.requeteValide();
    const sourceInstallation = this.lireInstallationDepuisRoute();
    if (sourceInstallation.estRouteInstallation && sourceInstallation.id == null) return;

    const silo = this.silos().find((item) => item.state === state);
    if (!silo || silo.loadingMore || silo.currentPage >= silo.totalPages) return;

    this.silos.set(
      this.silos().map((item) => (item.state === state ? { ...item, loadingMore: true } : item)),
    );

    const search = this.etat.filtreTexteTickets();
    const chargement: Promise<ResultatChargementSilo> =
      sourceInstallation.estRouteInstallation && sourceInstallation.id != null
        ? this.chargerPageSiloInstallation(
            sourceInstallation.id,
            silo,
            silo.currentPage + 1,
            true,
            search,
          )
        : (() => {
            const parc = this.etat.parcSelectionne();
            if (!parc) {
              return Promise.resolve({
                state: silo.state,
                total: silo.count,
                totalNonLus: silo.badgeCount,
                currentPage: silo.currentPage,
                totalPages: silo.totalPages,
                tickets: silo.tickets,
              });
            }
            return this.chargerPageSiloParc(parc.id, silo, silo.currentPage + 1, true, search);
          })();

    void chargement.then((resultat) => {
      if (!estValide()) return;
      this.silos.set(
        this.silos().map((item) =>
          item.state === state
            ? {
                ...item,
                count: resultat.total,
                badgeCount: resultat.totalNonLus,
                currentPage: resultat.currentPage,
                totalPages: resultat.totalPages,
                loadingMore: false,
                tickets: resultat.tickets,
                erreur: resultat.erreur,
              }
            : item,
        ),
      );
    });
  }

  async reessayerChargement(state: TicketColumnState) {
    const silo = this.silos().find((item) => item.state === state);
    if (!silo || this.chargement() || silo.loadingMore) return;
    if (silo.currentPage < silo.totalPages) {
      this.chargerPageSuivante(state);
      return;
    }
    const installationId = this.lireInstallationDepuisRoute().id;
    if (installationId != null) {
      await this.chargerTicketsParInstallation(installationId, this.etat.filtreTexteTickets());
    } else {
      const parc = this.etat.parcSelectionne();
      if (parc) await this.chargerTicketsParParc(parc.id, this.etat.filtreTexteTickets());
    }
  }

  onTicketDragStarted(fromState: TicketColumnState, ticket: TicketCardItem) {
    this.drag.onTicketDragStarted(fromState, ticket);
  }

  onTicketDragEnded() {
    this.drag.onTicketDragEnded();
  }

  onSiloDragEntered(state: TicketColumnState) {
    this.drag.onSiloDragEntered(state);
  }

  onSiloDragLeft(state: TicketColumnState) {
    this.drag.onSiloDragLeft(state);
  }

  async onSiloDropped(targetState: TicketColumnState) {
    const estValide = this.requeteValide();
    const updatedSilos = await this.drag.onSiloDropped(targetState, this.silos());
    if (updatedSilos && estValide()) {
      // Le serveur réapplique les filtres et le tri après le changement d’état.
      const installationId = this.lireInstallationDepuisRoute().id;
      if (installationId != null) {
        await this.chargerTicketsParInstallation(installationId, this.etat.filtreTexteTickets());
      } else {
        const parc = this.etat.parcSelectionne();
        if (parc) await this.chargerTicketsParParc(parc.id, this.etat.filtreTexteTickets());
      }
    }
  }

  onSiloInvalidDropped(targetState: TicketColumnState) {
    this.drag.onSiloInvalidDropped(targetState);
  }

  onTicketClicked(state: TicketColumnState, ticket: TicketCardItem) {
    if (!ticket.id) return;
    this.etat.ticketSelectionne.set(ticket);
    this.etat.siloEtatTicket.set(state);
    void this.router.navigate(['/ticket', ticket.id]);
  }

  private async chargerTicketsParParc(parcId: number, search = '') {
    await this.chargerTicketsDepuisSource(
      (silo) => this.chargerPageSiloParc(parcId, silo, this.ticketsPage, false, search),
      'Erreur chargement tickets parc',
    );
  }

  private async chargerTicketsParInstallation(installationId: number, search = '') {
    await this.chargerTicketsDepuisSource(
      (silo) =>
        this.chargerPageSiloInstallation(installationId, silo, this.ticketsPage, false, search),
      'Erreur chargement tickets installation',
    );
  }

  private async chargerTicketsDepuisSource(
    charger: (silo: TicketSilo) => Promise<ResultatChargementSilo>,
    messageErreur: string,
  ) {
    this.reinitialiserSilos();
    this.chargement.set(true);
    const estValide = this.requeteValide();
    const silosSnapshot = untracked(() => this.silos());
    try {
      const reponses = await Promise.all(silosSnapshot.map((silo) => charger(silo)));
      if (!estValide()) return;
      this.silos.set(
        silosSnapshot.map((silo) => {
          const resultat = reponses.find((item) => item.state === silo.state);
          const tickets = resultat?.tickets ?? [];
          return {
            ...silo,
            count: resultat?.total ?? tickets.length,
            badgeCount: resultat?.totalNonLus ?? 0,
            currentPage: resultat?.currentPage ?? this.ticketsPage,
            totalPages: resultat?.totalPages ?? 0,
            loadingMore: false,
            tickets,
            erreur: resultat?.erreur,
          };
        }),
      );
    } catch (error) {
      if (!estValide()) return;
      console.error(messageErreur, error);
      this.reinitialiserSilos();
    } finally {
      if (estValide()) this.chargement.set(false);
    }
  }

  private reinitialiserSilos() {
    ++this.chargementId;
    this.chargement.set(false);
    this.silos.set(
      untracked(() => this.silos()).map((silo) => ({
        ...silo,
        count: 0,
        badgeCount: 0,
        currentPage: 1,
        totalPages: 0,
        loadingMore: false,
        tickets: [],
        erreur: undefined,
      })),
    );
  }

  private async chargerPageSiloParc(
    parcId: number,
    silo: TicketSilo,
    page: number,
    append: boolean,
    search = '',
  ): Promise<ResultatChargementSilo> {
    try {
      const response = await this.parcService.getParcTickets(
        parcId,
        silo.state,
        page,
        this.ticketsLimit,
        search,
        this.optionsTickets(),
      );
      return this.construireResultatSilo(silo, page, append, response);
    } catch (error) {
      console.error(`Erreur chargement tickets parc pour l'etat ${silo.state}`, error);
      return this.siloEnErreur(silo, append, error);
    }
  }

  private async chargerPageSiloInstallation(
    installationId: number,
    silo: TicketSilo,
    page: number,
    append: boolean,
    search = '',
  ): Promise<ResultatChargementSilo> {
    try {
      const response = await this.installationService.getInstallationTickets(
        installationId,
        silo.state,
        page,
        this.ticketsLimit,
        search,
        this.optionsTickets(),
      );
      return this.construireResultatSilo(silo, page, append, response);
    } catch (error) {
      console.error(`Erreur chargement tickets installation pour l'etat ${silo.state}`, error);
      return this.siloEnErreur(silo, append, error);
    }
  }

  private construireResultatSilo(
    silo: TicketSilo,
    page: number,
    append: boolean,
    response: TicketsResponse,
  ): ResultatChargementSilo {
    if (!response) throw new ErreurApi('La réponse du serveur est invalide.', 'reponse_invalide');
    const nouveauxTickets = extraireTickets(response).map((ticket) => adapterTicket(ticket));
    return {
      state: silo.state,
      total: extraireTotalPagination(response) ?? 0,
      totalNonLus: response?.pagination?.tickets_messages_non_lus ?? 0,
      currentPage: extrairePagePagination(response) ?? page,
      totalPages: extraireTotalPagesPagination(response),
      tickets: append ? [...silo.tickets, ...nouveauxTickets] : nouveauxTickets,
    };
  }

  private siloEnErreur(silo: TicketSilo, append: boolean, erreur: unknown): ResultatChargementSilo {
    return {
      state: silo.state,
      total: append ? silo.count : 0,
      totalNonLus: append ? silo.badgeCount : 0,
      currentPage: append ? silo.currentPage : 1,
      totalPages: append ? silo.totalPages : 0,
      tickets: append ? silo.tickets : [],
      erreur: messageErreurApi(erreur),
    };
  }

  private lireInstallationDepuisRoute(): { estRouteInstallation: boolean; id: number | null } {
    const rawId = this.routeInstallationId();
    if (rawId == null) return { estRouteInstallation: false, id: null };
    const id = Number(rawId);
    if (!Number.isFinite(id) || id <= 0) return { estRouteInstallation: true, id: null };
    return { estRouteInstallation: true, id };
  }
}
